<div class="container-fluid mt-4">
  <div class="row mb-3">
    <div class="col">
      <h2>{{ 'SystemAdmin.systemNotifications' | translate }}</h2>
    </div>
    <div class="col-auto">
      <button class="btn btn-primary" (click)="openCreateModal()">
        <i class="bi bi-plus-circle me-1"></i> {{ 'SystemAdmin.addNotification' | translate }}
      </button>
    </div>
  </div>

  <div class="card">
    <div class="card-body">
      <div *ngIf="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <konti-smart-grid
        *ngIf="!isLoading"
        [data]="notifications"
        [columns]="gridColumns"
        [pageSize]="25"
        [showGroupPanel]="false"
      >
        <ng-template appSmartGridCell="type" let-row>
          <span class="badge" [ngClass]="getTypeBadgeClass(row.type)">
            <i [class]="getTypeIcon(row.type)"></i> {{ ('SystemAdmin.type' + row.type.charAt(0).toUpperCase() + row.type.slice(1)) | translate }}
          </span>
        </ng-template>

        <ng-template appSmartGridCell="startDate" let-row>
          {{ row.startDate | date:'dd.MM.yyyy HH:mm' }}
        </ng-template>

        <ng-template appSmartGridCell="endDate" let-row>
          {{ row.endDate ? (row.endDate | date:'dd.MM.yyyy HH:mm') : '-' }}
        </ng-template>

        <ng-template appSmartGridCell="isActive" let-row>
          <span class="badge" [ngClass]="row.isActive ? 'badge-soft-success' : 'badge-soft-secondary'">
            {{ (row.isActive ? 'Common.Active' : 'Common.inactive') | translate }}
          </span>
        </ng-template>

        <ng-template appSmartGridCell="clientCount" let-row>
          <span *ngIf="row.clientCount === 0" class="badge badge-soft-primary">
            <i class="bi bi-globe"></i> {{ 'SystemAdmin.allClients' | translate }}
          </span>
          <span *ngIf="row.clientCount > 0" class="badge badge-soft-info">
            {{ row.clientCount }} {{ 'SystemAdmin.specificClients' | translate }}
          </span>
        </ng-template>

        <ng-template appSmartGridCell="actions" let-row>
          <div class="action-capsule btn-group btn-group-sm sg-desktop-only">
            <button class="btn btn-outline-secondary" (click)="openEditModal(row)" [title]="'General.edit' | translate">
              <i class="bi bi-pencil" aria-hidden="true"></i>
              <span class="visually-hidden">{{ 'General.edit' | translate }}</span>
            </button>
            <button class="btn btn-outline-danger action-danger" (click)="openDeleteModal(row)" [title]="'General.delete' | translate">
              <i class="bi bi-trash3" aria-hidden="true"></i>
              <span class="visually-hidden">{{ 'General.delete' | translate }}</span>
            </button>
          </div>
        </ng-template>
      </konti-smart-grid>
    </div>
  </div>
</div>

<!-- Notification Create/Edit Modal -->
<ng-template #notificationModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">
      {{ (isEditMode ? 'SystemAdmin.editNotification' : 'SystemAdmin.addNotification') | translate }}
    </h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()" [disabled]="isSaving"></button>
  </div>
  <div class="modal-body">
    <form [formGroup]="notificationForm">
      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label">{{ 'SystemAdmin.notificationTitle' | translate }} <span class="text-danger">*</span></label>
          <input type="text" class="form-control" formControlName="title" maxlength="200" [class.is-invalid]="notificationForm.get('title')?.invalid && notificationForm.get('title')?.touched">
          <div class="invalid-feedback" *ngIf="notificationForm.get('title')?.invalid && notificationForm.get('title')?.touched">
            {{ 'Common.requiredField' | translate }}
          </div>
        </div>

        <div class="col-md-3 mb-3">
          <label class="form-label">{{ 'SystemAdmin.notificationType' | translate }} <span class="text-danger">*</span></label>
          <select class="form-select" formControlName="type">
            <option *ngFor="let type of notificationTypes" [value]="type.value">
              {{ ('SystemAdmin.type' + type.label) | translate }}
            </option>
          </select>
        </div>

        <div class="col-md-3 mb-3">
          <label class="form-label">{{ 'SystemAdmin.notificationPriority' | translate }}</label>
          <input type="number" class="form-control" formControlName="priority" min="0" max="100">
          <small class="form-text text-muted">0-100 (høyere = viktigere)</small>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label">{{ 'SystemAdmin.displayType' | translate }}</label>
          <div class="d-flex gap-3 mt-1">
            <div class="form-check">
              <input class="form-check-input" type="radio" formControlName="displayType" value="alert" id="displayTypeAlert">
              <label class="form-check-label" for="displayTypeAlert">
                <i class="bi bi-bar-chart-steps me-1"></i>{{ 'SystemAdmin.displayTypeAlert' | translate }}
              </label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="radio" formControlName="displayType" value="modal" id="displayTypeModal">
              <label class="form-check-label" for="displayTypeModal">
                <i class="bi bi-window me-1"></i>{{ 'SystemAdmin.displayTypeModal' | translate }}
              </label>
            </div>
          </div>
        </div>

        <div class="col-md-6 mb-3">
          <label class="form-label" for="targetAudience">{{ 'SystemAdmin.targetAudience' | translate }}</label>
          <select class="form-select" formControlName="targetAudience" id="targetAudience">
            <option value="All">{{ 'SystemAdmin.audienceAll' | translate }}</option>
            <option value="ManagersOnly">{{ 'SystemAdmin.audienceManagersOnly' | translate }}</option>
            <option value="EmployeesOnly">{{ 'SystemAdmin.audienceEmployeesOnly' | translate }}</option>
          </select>
        </div>
      </div>

      <div *ngIf="notificationForm.get('displayType')?.value === 'modal'" class="row">
        <div class="col-12 mb-3">
          <label class="form-label" for="imageUrl">{{ 'SystemAdmin.imageUrl' | translate }}</label>
          <input type="url" class="form-control" formControlName="imageUrl" id="imageUrl"
                 [placeholder]="'SystemAdmin.imageUrlPlaceholder' | translate">
          <small class="form-text text-muted">{{ 'SystemAdmin.imageUrlHint' | translate }}</small>
        </div>

        <div class="col-md-5 mb-3">
          <label class="form-label" for="ctaText">{{ 'SystemAdmin.ctaText' | translate }}</label>
          <input type="text" class="form-control" formControlName="ctaText" id="ctaText"
                 [placeholder]="'SystemAdmin.ctaTextPlaceholder' | translate" maxlength="100">
        </div>

        <div class="col-md-7 mb-3">
          <label class="form-label" for="ctaLink">{{ 'SystemAdmin.ctaLink' | translate }}</label>
          <input type="url" class="form-control" formControlName="ctaLink" id="ctaLink"
                 [placeholder]="'SystemAdmin.ctaLinkPlaceholder' | translate">
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label">{{ 'SystemAdmin.notificationMessage' | translate }} <span class="text-danger">*</span></label>
        <textarea class="form-control" formControlName="message" rows="4" [class.is-invalid]="notificationForm.get('message')?.invalid && notificationForm.get('message')?.touched"></textarea>
        <div class="invalid-feedback" *ngIf="notificationForm.get('message')?.invalid && notificationForm.get('message')?.touched">
          {{ 'Common.requiredField' | translate }}
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label">{{ 'SystemAdmin.startDate' | translate }} <span class="text-danger">*</span></label>
          <input type="datetime-local" class="form-control" formControlName="startDate" [class.is-invalid]="notificationForm.get('startDate')?.invalid && notificationForm.get('startDate')?.touched">
          <div class="invalid-feedback" *ngIf="notificationForm.get('startDate')?.invalid && notificationForm.get('startDate')?.touched">
            {{ 'Common.requiredField' | translate }}
          </div>
        </div>

        <div class="col-md-6 mb-3">
          <label class="form-label">{{ 'SystemAdmin.endDate' | translate }}</label>
          <input type="datetime-local" class="form-control" formControlName="endDate">
          <small class="form-text text-muted">Valgfri (ingen sluttdato = permanent)</small>
        </div>
      </div>

      <div class="mb-3">
        <div class="form-check form-switch">
          <input class="form-check-input" type="checkbox" formControlName="isActive" id="isActiveSwitch">
          <label class="form-check-label" for="isActiveSwitch">
            {{ 'Common.Active' | translate }}
          </label>
        </div>
      </div>

      <hr>

      <div class="mb-3">
        <label class="form-label fw-bold">{{ 'SystemAdmin.clientAssignment' | translate }}</label>
        
        <div class="form-check mb-3">
          <input class="form-check-input" type="checkbox" [checked]="isAllClients" (change)="toggleAllClients()" id="allClientsCheckbox">
          <label class="form-check-label" for="allClientsCheckbox">
            <i class="bi bi-globe"></i> {{ 'SystemAdmin.allClients' | translate }}
          </label>
          <small class="form-text text-muted d-block">Vis varselet til alle kunder</small>
        </div>

        <div *ngIf="!isAllClients" class="border rounded p-3" style="max-height: 200px; overflow-y: auto;">
          <div class="form-check" *ngFor="let domain of availableDomains">
            <input class="form-check-input" type="checkbox" 
                   [checked]="isClientSelected(domain.databaseName)" 
                   (change)="toggleClientSelection(domain.databaseName)"
                   [id]="'client-' + domain.domainID">
            <label class="form-check-label" [for]="'client-' + domain.domainID">
              {{ domain.domainName }} ({{ domain.databaseName }})
            </label>
          </div>
          <div *ngIf="availableDomains.length === 0" class="text-muted text-center">
            {{ 'Common.noData' | translate }}
          </div>
        </div>

        <small class="form-text text-muted" *ngIf="!isAllClients">
          Velg hvilke kunder som skal se varselet
        </small>
      </div>
    </form>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="modal.dismiss()" [disabled]="isSaving">
      {{ 'Common.cancel' | translate }}
    </button>
    <button type="button" class="btn btn-primary" (click)="saveNotification()" [disabled]="isSaving">
      <span *ngIf="isSaving" class="spinner-border spinner-border-sm me-2"></span>
      {{ (isEditMode ? 'Common.update' : 'Common.create') | translate }}
    </button>
  </div>
</ng-template>

<!-- Delete Confirmation Modal -->
<ng-template #deleteConfirmModal let-modal>
  <div class="modal-header bg-danger text-white">
    <h5 class="modal-title">{{ 'SystemAdmin.confirmDeleteNotification' | translate }}</h5>
    <button type="button" class="btn-close btn-close-white" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <p>{{ 'SystemAdmin.deleteNotificationWarning' | translate }}</p>
    <p><strong>{{ selectedNotification?.title }}</strong></p>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">
      {{ 'Common.cancel' | translate }}
    </button>
    <button type="button" class="btn btn-danger" (click)="confirmDelete()">
      {{ 'Common.delete' | translate }}
    </button>
  </div>
</ng-template>
